import { Box, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useDeletePartyMutation } from "Api/Mutations/Party/useDeletePartyMutation";
import { usePartiesQueries } from "Api/Queries/Party/usePartiesQueries";
import { getPartyQueryKey } from "Api/Queries/Party/usePartyQuery";
import type { CardListItemType } from "Components/Shared/CardsList/CardsList";
import { ControlledCardsList } from "Components/Shared/CardsList/ControlledCardsList";
import { ControlledCardsListSkeleton } from "Components/Shared/CardsList/ControlledCardsListSkeleton";
import { ModalDialog } from "Components/Shared/Dialogs/ModalDialog";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { ContactPersonDetail } from "Components/Shared/Merchant/ContactPersons/ContactPersonDetail";
import { ContactPersonForm } from "Components/Shared/Merchant/ContactPersons/ContactPersonForm";
import type { PartyDto } from "Infrastructure/Api/Api";
import { formatPhone } from "Utils/PhoneUtils";
import { useState } from "react";
import { toast } from "sonner";
import { useResource } from "Translations/Resources";
import { Resources } from "Translations/Resources";

const COMPONENT_RESOURCES = Resources.Components.Merchant.ContactPersons;

type Props = {
  relatedPartiesIDs: Array<string>;
  owningPartyID: string;
  isReadOnly?: boolean;
  noItemsText?: string;
};

export const ContactPersonsContent = ({
  relatedPartiesIDs,
  owningPartyID,
  isReadOnly,
  noItemsText,
}: Props) => {
  const [selectedContactPerson, setSelectedContactPerson] =
    useState<PartyDto | null>(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isUpdateOpen, setIsUpdateOpen] = useState(false);
  const { t } = useResource();
  const queryClient = useQueryClient();
  const { mutate: deleteParty, isPending: isDeletePending } =
    useDeletePartyMutation({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getPartyQueryKey(owningPartyID),
        });
        toast.success(t(COMPONENT_RESOURCES.DeleteSuccess));
      },
      onError: () => {
        toast.error(t(COMPONENT_RESOURCES.DeleteError));
      },
    });

  const { data, isPending } = usePartiesQueries(relatedPartiesIDs);

  if (isPending) {
    return <ControlledCardsListSkeleton isReadOnly={isReadOnly} />;
  }

  if (!data) {
    return <ErrorText>{t(COMPONENT_RESOURCES.LoadingError)}</ErrorText>;
  }

  const listItems = data
    .map((contactPerson) => {
      const { firstName, lastName, phone, email, publicID } = contactPerson;

      if (!publicID) {
        return null;
      }

      const phoneValue = formatPhone(phone);

      return {
        id: publicID,
        title: (
          <Box display="flex" gap={1} alignItems="center">
            <Typography fontWeight="500" fontSize="1rem">
              {firstName} {lastName}
            </Typography>
          </Box>
        ),
        subtitle: phoneValue && email ? `${phoneValue}, ${email}` : undefined,
        isDisabled: isDeletePending,
        ...(isReadOnly
          ? {
              onView: () => {
                setSelectedContactPerson(contactPerson);
                setIsViewOpen(true);
              },
            }
          : {
              onEdit: () => {
                setSelectedContactPerson(contactPerson);
                setIsUpdateOpen(true);
              },
              onDelete: () => {
                confirm(
                  t(COMPONENT_RESOURCES.DeleteConfirmation, {
                    name: `${firstName} ${lastName}`,
                  }),
                ) && deleteParty(publicID);
              },
            }),
      };
    })
    .filter((item) => !!item) as Array<CardListItemType>; // ts build throws error without the explicit type casting;

  return (
    <>
      <ControlledCardsList
        listItems={listItems}
        isReadOnly={isReadOnly}
        addButtonConfig={{
          onClick: () => {
            setSelectedContactPerson(null);
            setIsUpdateOpen(true);
          },
          text: t(COMPONENT_RESOURCES.AddContactPerson),
        }}
        noItemsText={noItemsText ?? t(COMPONENT_RESOURCES.NoContactPersons)}
      />
      <ModalDialog
        isOpen={isUpdateOpen}
        onClose={() => setIsUpdateOpen(false)}
        title={t(COMPONENT_RESOURCES.NewContactPerson)}
      >
        <ContactPersonForm
          contactPerson={selectedContactPerson || {}}
          currentOtherNationalities={selectedContactPerson?.otherNationalities}
          owningPartyID={owningPartyID}
          onAfterSubmit={(isSuccess) => {
            if (isSuccess) {
              setSelectedContactPerson(null);
              setIsUpdateOpen(false);
            }
          }}
        />
      </ModalDialog>
      <ModalDialog
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        title={t(COMPONENT_RESOURCES.ContactPersonDetail)}
      >
        {selectedContactPerson && (
          <ContactPersonDetail contactPerson={selectedContactPerson} />
        )}
      </ModalDialog>
    </>
  );
};
